"use client";

import { useEffect, useRef } from "react";
import { X } from "lucide-react";

export default function BaseModal({
  title,
  description = "",
  onClose,
  children,
  maxWidthClassName = "max-w-lg",
  bodyClassName = "",
  compactScrollable = false,
}) {
  const contentRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Focus first interactive element and restore focus on close
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusable = contentRef.current?.querySelector(
      "input, button, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    focusable?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;
      const focusableElements = Array.from(
        contentRef.current?.querySelectorAll(
          "input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"
        ) || []
      );
      if (!focusableElements.length) return;
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const handleBackdropClick = () => {
    onClose?.();
  };

  const handleContentClick = (event) => {
    event.stopPropagation();
  };

  return (
    <div
      className={compactScrollable
        ? "fixed inset-0 z-[9999] flex items-center justify-center bg-transparent p-3 sm:p-6"
        : "fixed inset-0 z-[9999] flex items-center justify-center bg-transparent px-4 py-8"}
      data-allow-overlay="1"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        className="absolute inset-0"
        data-modal-backdrop
        style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
      />

      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={compactScrollable
          ? `relative z-10 flex max-h-[calc(100dvh-1.5rem)] min-h-0 w-full bg-transparent text-slate-900 sm:max-h-[calc(100dvh-3rem)] ${maxWidthClassName}`
          : `relative z-10 w-full max-h-[80vh] bg-transparent text-slate-900 ${maxWidthClassName}`}
        onClick={handleContentClick}
      >
        <div
          className={compactScrollable
            ? "flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_70px_-24px_rgba(15,23,42,0.5)]"
            : "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_25px_80px_-12px_rgba(0,0,0,0.25)]"}
          data-modal-panel
        >
          <div className={compactScrollable
            ? "relative flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6"
            : "relative flex items-start justify-between gap-4 p-6"}>
            <div className="space-y-1">
              {title ? (
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p className="text-sm text-slate-600 leading-relaxed">
                  {description}
                </p>
              ) : null}
            </div>

            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="h-9 w-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition active:scale-95"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            className={compactScrollable
              ? `relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 sm:px-6 sm:py-5 ${bodyClassName}`
              : `relative overflow-y-auto px-6 pb-6 ${bodyClassName}`}
            data-modal-scroll-body
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
